import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Switch,
  Space,
  Modal,
  Popconfirm,
  Tag,
  Tooltip,
  Collapse,
  Table,
  Spin,
  Empty,
  Upload,
  Avatar,
  Slider,
  Divider,
  Typography,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  HolderOutlined,
  CopyOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getModelListUnified, getUnifiedMinVersion } from '../../graphql/queries';
import {
  updateCreditConfigUnified,
  getSignedUrl,
  getObjectUrl,
  updateUnifiedMinVersion,
} from '../../graphql/mutation';
import { uploadToR2 } from '../../utils/uploadToR2';

const { Title } = Typography;
const { Option } = Select;

const AspectRatioEnum = {
  WIDESCREEN: { name: 'Widescreen', ratio: '16:9', x: 16, y: 9 },
  PORTRAIT: { name: 'Portrait', ratio: '9:16', x: 9, y: 16 },
  SQUARE: { name: 'Square', ratio: '1:1', x: 1, y: 1 },
  FULL_SCREEN: { name: 'Full Screen', ratio: '4:3', x: 4, y: 3 },
  TABLET: { name: 'Tablet', ratio: '3:4', x: 3, y: 4 },
  ULTRAWIDE: { name: 'Ultrawide', ratio: '21:9', x: 21, y: 9 },
};

const VideoSecondsEnum = {
  ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5, SIX: 6,
  SEVEN: 7, EIGHT: 8, NINE: 9, TEN: 10, ELEVEN: 11, TWELVE: 12,
  THIRTEEN: 13, FOURTEEN: 14, FIFTEEN: 15, SIXTEEN: 16, SEVENTEEN: 17,
  EIGHTEEN: 18, NINETEEN: 19, TWENTY: 20, TWENTYONE: 21, TWENTYTWO: 22,
  TWENTYTHREE: 23, TWENTYFOUR: 24, TWENTYFIVE: 25, TWENTYSIX: 26,
  TWENTYSEVEN: 27, TWENTYEIGHT: 28, TWENTYNINE: 29, THIRTY: 30,
};

const VideoQualityEnum = {
  LOW: { name: '360p', width: 640, height: 360 },
  NORMAL: { name: '480p', width: 864, height: 480 },
  STANDARD: { name: '512p', width: 910, height: 512 },
  MEDIUM: { name: '540p', width: 960, height: 540 },
  ENHANCED: { name: '768p', width: 1365, height: 768 },
  HIGH: { name: '720p', width: 1280, height: 720 },
  ULTRA: { name: '1080p', width: 1920, height: 1080 },
};

const QUALITY_ORDER = ['LOW', 'NORMAL', 'STANDARD', 'MEDIUM', 'ENHANCED', 'HIGH', 'ULTRA'];

const getResolutionOptionsOrdered = () =>
  Object.keys(VideoQualityEnum).sort(
    (a, b) => QUALITY_ORDER.indexOf(a) - QUALITY_ORDER.indexOf(b)
  );

const SortableModelWrapper = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'stretch',
        marginBottom: 16,
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        title="Drag to reorder"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          cursor: isDragging ? 'grabbing' : 'grab',
          color: '#bfbfbf',
          background: '#fafafa',
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        <HolderOutlined />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
};

const EMPTY_MODEL_DIALOG = {
  visible: false,
  modelId: '',
  modelName: '',
  modelDescription: '',
  modelEndpoint: '',
  icon: '',
  iconFile: null,
  costPreview: 1,
  hasAudioFeature: false,
  isAudioOptional: true,
  isDefault: false,
  isFreeCropImage: false,
  badge: null,
  defaultValues: {
    isAudio: false,
    duration: null,
    resolution: null,
    aspectRatio: null,
  },
  pricingRows: [], // [{ resKey: string|null, audioOn: number|null, audioOff: number|null }]
};

export const UnifiedCreditConfig = () => {
  const [unifiedConfig, setUnifiedConfig] = useState({ models: [] });
  const [addModelDialog, setAddModelDialog] = useState(EMPTY_MODEL_DIALOG);
  const [editModelDialog, setEditModelDialog] = useState({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 });
  const [minVersionModalVisible, setMinVersionModalVisible] = useState(false);
  const [minVersion, setMinVersion] = useState('');
  const [minVersionInput, setMinVersionInput] = useState('');

  const { data: minVersionData } = useQuery(getUnifiedMinVersion);
  const [updateMinVersionMutation] = useMutation(updateUnifiedMinVersion);

  useEffect(() => {
    if (minVersionData?.getUnifiedMinVersion !== undefined) {
      const v = minVersionData.getUnifiedMinVersion ?? '';
      setMinVersion(v);
      setMinVersionInput(v);
    }
  }, [minVersionData]);

  const handleSaveMinVersion = async () => {
    try {
      await updateMinVersionMutation({ variables: { version: minVersionInput } });
      setMinVersion(minVersionInput);
      setMinVersionModalVisible(false);
      message.success('Minimum unified config version updated.');
    } catch (err) {
      message.error('Failed to update: ' + err.message);
    }
  };

  const modelOrderRef = useRef(
    (() => {
      try { return JSON.parse(localStorage.getItem('unifiedModelOrder')) || null; } catch { return null; }
    })()
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const { data, loading, refetch } = useQuery(getModelListUnified);
  const [updateMutation] = useMutation(updateCreditConfigUnified);
  const [getSignedUrlMutation] = useMutation(getSignedUrl);
  const [getObjectUrlMutation] = useMutation(getObjectUrl);

  const saveOrder = (ids) => {
    modelOrderRef.current = ids;
    try { localStorage.setItem('unifiedModelOrder', JSON.stringify(ids)); } catch {}
  };

  const applyOrder = (models) => {
    const order = modelOrderRef.current;
    if (!order || !order.length) return models;
    return [...models].sort((a, b) => {
      const ai = order.indexOf(a.id);
      const bi = order.indexOf(b.id);
      return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
    });
  };

  useEffect(() => {
    if (data?.getModelListUnified) {
      const raw = JSON.parse(JSON.stringify(data.getModelListUnified));
      setUnifiedConfig(prev => {
        const prevModels = prev.models || [];
        const models = (raw.models || []).map((m) => {
          const existing = prevModels.find(em => em.id === m.id);
          return {
            ...m,
            has_audio_feature: m.has_audio_feature ?? false,
            is_audio_optional: m.is_audio_optional ?? true,
            is_default: m.is_default ?? false,
            pricing_by_resolution: m.pricing_by_resolution ?? existing?.pricing_by_resolution,
          };
        });
        return { models: applyOrder(models) };
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleModelDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setUnifiedConfig((prev) => {
      const oldIdx = prev.models.findIndex((m) => m.id === active.id);
      const newIdx = prev.models.findIndex((m) => m.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return prev;
      const reordered = arrayMove(prev.models, oldIdx, newIdx);
      saveOrder(reordered.map((m) => m.id));
      return { models: reordered };
    });
  };

  // ---- Icon upload helper ----

  const uploadIcon = async (iconFile) => {
    const { data: signedUrlData } = await getSignedUrlMutation({
      variables: { fileName: iconFile.name },
    });
    const uploadResult = await uploadToR2(
      iconFile,
      signedUrlData.getSignedUrl.url,
      signedUrlData.getSignedUrl.contentType
    );
    if (!uploadResult.success) throw new Error(uploadResult.message);
    const { data: objectUrlData } = await getObjectUrlMutation({
      variables: { key: signedUrlData.getSignedUrl.id, eTag: 'etag' },
    });
    return objectUrlData.getObjectUrl;
  };

  // ---- CRUD: Models ----

  const countDefaultModels = () =>
    (unifiedConfig.models || []).filter((m) => m.is_default === true).length;

  const buildDefaultValues = (defaultValues, hasAudioFeature) => {
    const dv = {};
    if (defaultValues?.duration) dv.duration = defaultValues.duration;
    if (defaultValues?.resolution) dv.resolution = defaultValues.resolution;
    if (defaultValues?.aspectRatio) dv.aspectRatio = defaultValues.aspectRatio;
    if (defaultValues?.isAudio !== undefined) dv.isAudio = defaultValues.isAudio;
    return Object.keys(dv).length > 0 ? dv : null;
  };

  const handleAddModel = async () => {
    const d = addModelDialog;
    if (!d.modelId || !d.modelName) {
      message.error('Please fill in Model ID and Model Name');
      return;
    }
    if (d.isDefault && countDefaultModels() >= 2) {
      message.error('Cannot mark more than 2 models as default. Please unmark another default model first.');
      return;
    }
    try {
      let iconUrl = d.icon;
      if (d.iconFile) iconUrl = await uploadIcon(d.iconFile);

      const defaultValues = d.isDefault ? buildDefaultValues(d.defaultValues, d.hasAudioFeature) : null;
      const newModel = {
        id: d.modelId,
        name: d.modelName,
        description: d.modelDescription || '',
        model_endpoint: d.modelEndpoint || '',
        icon: iconUrl || '',
        cost_preview: d.costPreview || 1,
        has_audio_feature: d.hasAudioFeature,
        is_audio_optional: d.isAudioOptional,
        is_default: d.isDefault,
        is_free_crop_image: d.isFreeCropImage,
        badge: d.badge || null,
        pricing_by_resolution: buildPricingMap(d.pricingRows, d.hasAudioFeature, d.isAudioOptional),
        aspect_ratios: [],
      };
      if (defaultValues) newModel.default_values = defaultValues;

      setUnifiedConfig((prev) => ({ models: [...prev.models, newModel] }));
      setAddModelDialog(EMPTY_MODEL_DIALOG);
      message.success('Model added');
    } catch (err) {
      message.error('Failed to add model: ' + err.message);
    }
  };

  const handleEditModel = async () => {
    const d = editModelDialog;
    if (!d.modelId || !d.modelName) {
      message.error('Please fill in Model ID and Model Name');
      return;
    }
    if (d.isDefault) {
      const currentModel = unifiedConfig.models[d.modelIndex];
      if (!currentModel?.is_default && countDefaultModels() >= 2) {
        message.error('Cannot mark more than 2 models as default. Please unmark another default model first.');
        return;
      }
    }
    try {
      let iconUrl = d.icon;
      if (d.iconFile) iconUrl = await uploadIcon(d.iconFile);

      setUnifiedConfig((prev) => {
        const newModels = JSON.parse(JSON.stringify(prev.models));
        const model = newModels[d.modelIndex];
        const newPricing = buildPricingMap(d.pricingRows, d.hasAudioFeature, d.isAudioOptional);
        const defaultValues = d.isDefault ? buildDefaultValues(d.defaultValues, d.hasAudioFeature) : null;

        newModels[d.modelIndex] = {
          ...model,
          id: d.modelId,
          name: d.modelName,
          description: d.modelDescription || '',
          model_endpoint: d.modelEndpoint || '',
          icon: iconUrl || '',
          cost_preview: d.costPreview || 1,
          has_audio_feature: d.hasAudioFeature,
          is_audio_optional: d.isAudioOptional,
          is_default: d.isDefault,
          is_free_crop_image: d.isFreeCropImage,
          badge: d.badge || null,
          default_values: defaultValues,
          pricing_by_resolution: newPricing,
        };

        newModels[d.modelIndex].aspect_ratios?.forEach(ar => {
          ar.resolutions?.forEach(res => {
            const resKey = res.resolution?.api_enum_value;
            if (resKey && newPricing[resKey] !== undefined) {
              res.pricing_per_second = newPricing[resKey];
            }
          });
        });

        return { models: newModels };
      });

      setEditModelDialog({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 });
      message.success('Model updated');
    } catch (err) {
      message.error('Failed to update model: ' + err.message);
    }
  };

  const handleRemoveModel = (modelIndex) => {
    setUnifiedConfig((prev) => ({
      models: prev.models.filter((_, i) => i !== modelIndex),
    }));
  };

  // ---- CRUD: Aspect Ratios ----

  const handleToggleAspectRatioGlobal = (modelIndex, ratioKey) => {
    const model = unifiedConfig.models[modelIndex];
    const activeRatioKeys = new Set(model.aspect_ratios?.map(ar => ar.ratio?.api_enum_value) || []);
    if (activeRatioKeys.has(ratioKey)) {
      const ratioIndex = model.aspect_ratios.findIndex(ar => ar.ratio?.api_enum_value === ratioKey);
      if (ratioIndex !== -1) handleRemoveAspectRatio(modelIndex, ratioIndex);
    } else {
      const ratioInfo = AspectRatioEnum[ratioKey];
      if (!ratioInfo) return;
      const templateResolutions = model.aspect_ratios?.[0]?.resolutions
        ? JSON.parse(JSON.stringify(model.aspect_ratios[0].resolutions))
        : [];
      const newRatio = {
        ratio: { api_enum_value: ratioKey, name: ratioInfo.ratio, short_name: ratioInfo.ratio, aspect_ratio_x: ratioInfo.x, aspect_ratio_y: ratioInfo.y },
        resolutions: templateResolutions,
      };
      setUnifiedConfig((prev) => {
        const nm = JSON.parse(JSON.stringify(prev.models));
        nm[modelIndex].aspect_ratios = [...(nm[modelIndex].aspect_ratios || []), newRatio];
        return { models: nm };
      });
    }
  };

  const handleRemoveAspectRatio = (modelIndex, ratioIndex) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].aspect_ratios.splice(ratioIndex, 1);
      return { models: nm };
    });
  };

  // ---- CRUD: Resolutions ----

  const handleToggleResolutionGlobal = (modelIndex, resKey) => {
    const model = unifiedConfig.models[modelIndex];
    const activeResolutionKeys = new Set();
    model.aspect_ratios?.forEach(ar => ar.resolutions?.forEach(r => activeResolutionKeys.add(r.resolution?.api_enum_value)));
    if (activeResolutionKeys.has(resKey)) {
      setUnifiedConfig((prev) => {
        const nm = JSON.parse(JSON.stringify(prev.models));
        nm[modelIndex].aspect_ratios?.forEach(ar => {
          ar.resolutions = (ar.resolutions || []).filter(r => r.resolution?.api_enum_value !== resKey);
        });
        return { models: nm };
      });
    } else {
      const qualityInfo = VideoQualityEnum[resKey];
      if (!qualityInfo) return;
      const activeDurationKeys = new Set();
      model.aspect_ratios?.forEach(ar => ar.resolutions?.forEach(res => res.durations?.forEach(d => activeDurationKeys.add(d.duration?.api_enum_value))));
      const durations = [...activeDurationKeys]
        .map(dk => ({
          duration: { api_enum_value: dk, seconds: VideoSecondsEnum[dk], name: `${VideoSecondsEnum[dk]} seconds`, short_name: `${VideoSecondsEnum[dk]}s` },
          cost: 0,
        }))
        .sort((a, b) => a.duration.seconds - b.duration.seconds);
      const savedPricing = (model.pricing_by_resolution || {})[resKey];
      const newRes = {
        resolution: { api_enum_value: resKey, name: qualityInfo.name },
        pricing_per_second: savedPricing !== undefined ? savedPricing : 0,
        durations,
      };
      setUnifiedConfig((prev) => {
        const nm = JSON.parse(JSON.stringify(prev.models));
        nm[modelIndex].aspect_ratios?.forEach(ar => {
          if (!ar.resolutions?.some(r => r.resolution?.api_enum_value === resKey)) {
            ar.resolutions = [...(ar.resolutions || []), JSON.parse(JSON.stringify(newRes))];
          }
        });
        return { models: nm };
      });
    }
  };

  const handleRemoveResolution = (modelIndex, ratioIndex, resIndex) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].aspect_ratios[ratioIndex].resolutions.splice(resIndex, 1);
      return { models: nm };
    });
  };

  // ---- CRUD: Durations ----

  const handleToggleDurationGlobal = (modelIndex, durationKey) => {
    const model = unifiedConfig.models[modelIndex];
    const activeDurationKeys = new Set();
    model.aspect_ratios?.forEach(ar => ar.resolutions?.forEach(res => res.durations?.forEach(d => activeDurationKeys.add(d.duration?.api_enum_value))));
    const seconds = VideoSecondsEnum[durationKey];
    if (seconds === undefined) return;
    if (activeDurationKeys.has(durationKey)) {
      setUnifiedConfig((prev) => {
        const nm = JSON.parse(JSON.stringify(prev.models));
        nm[modelIndex].aspect_ratios?.forEach(ar => {
          ar.resolutions?.forEach(res => {
            res.durations = (res.durations || []).filter(d => d.duration?.api_enum_value !== durationKey);
          });
        });
        return { models: nm };
      });
    } else {
      const newDur = {
        duration: { api_enum_value: durationKey, seconds, name: `${seconds} seconds`, short_name: `${seconds}s` },
        cost: 0,
      };
      setUnifiedConfig((prev) => {
        const nm = JSON.parse(JSON.stringify(prev.models));
        nm[modelIndex].aspect_ratios?.forEach(ar => {
          ar.resolutions?.forEach(res => {
            if (!res.durations?.some(d => d.duration?.api_enum_value === durationKey)) {
              res.durations = [...(res.durations || []), JSON.parse(JSON.stringify(newDur))].sort(
                (a, b) => a.duration.seconds - b.duration.seconds
              );
            }
          });
        });
        return { models: nm };
      });
    }
  };

  const handleRemoveDuration = (modelIndex, ratioIndex, resIndex, durIndex) => {
    setUnifiedConfig((prev) => {
      const nm = JSON.parse(JSON.stringify(prev.models));
      nm[modelIndex].aspect_ratios[ratioIndex].resolutions[resIndex].durations.splice(durIndex, 1);
      return { models: nm };
    });
  };

  // ---- Save ----

  const handleSave = async () => {
    try {
      saveOrder(unifiedConfig.models.map((m) => m.id));
      const configToSave = unifiedConfig;
      await updateMutation({ variables: { creditJson: configToSave } });
      message.success('Saved. Recalculating costs...');
      const refreshed = await refetch();
      if (refreshed?.data?.getModelListUnified) {
        const raw = JSON.parse(JSON.stringify(refreshed.data.getModelListUnified));
        const currentModels = unifiedConfig.models;
        const models = applyOrder(raw.models || []).map(m => {
          const existing = currentModels.find(em => em.id === m.id);
          return existing?.pricing_by_resolution
            ? { ...m, pricing_by_resolution: existing.pricing_by_resolution }
            : m;
        });
        setUnifiedConfig({ models });
      }
      message.success('Unified configuration saved & costs recalculated.');
    } catch (err) {
      message.error('Save failed: ' + err.message);
    }
  };

  // ---- Duration table columns ----

  const createDurationColumns = (modelIndex, ratioIndex, resIndex, hasAudio, isAudioOptional) => {
    const costCols =
      hasAudio && isAudioOptional
        ? [
            {
              title: 'Audio Off ',
              key: 'cost_off',
              width: 150,
              render: (_, record) => (
                <InputNumber
                  value={record?.cost?.without_audio ?? 0}
                  disabled
                  min={0}
                  style={{ width: '100%' }}
                  prefix="💎"
                />
              ),
            },
            {
              title: 'Audio On',
              key: 'cost_on',
              width: 150,
              render: (_, record) => (
                <InputNumber
                  value={record?.cost?.with_audio ?? 0}
                  disabled
                  min={0}
                  style={{ width: '100%' }}
                  prefix="💎"
                />
              ),
            },
          ]
        : [
            {
              title: 'Cost (Credits)',
              key: 'cost',
              width: 150,
              render: (_, record) => (
                <InputNumber
                  value={record?.cost ?? 0}
                  disabled
                  min={0}
                  style={{ width: '100%' }}
                  prefix="💎"
                />
              ),
            },
          ];

    return [
      {
        title: 'Duration',
        key: 'duration',
        render: (_, record) => (
          <Space>
            <ThunderboltOutlined style={{ color: '#1890ff' }} />
            <strong>
              {record.duration?.short_name}
            </strong>
          </Space>
        ),
      },
      ...costCols,
      {
        title: 'API Value',
        dataIndex: ['duration', 'api_enum_value'],
        key: 'api_value',
        render: (text) => <Tag color="blue">{text}</Tag>,
      },
      {
        title: 'Action',
        key: 'action',
        width: 100,
        render: (_, record, index) => (
          <Popconfirm
            title="Remove Duration"
            description={`Remove ${record.duration?.short_name}?`}
            onConfirm={() => handleRemoveDuration(modelIndex, ratioIndex, resIndex, index)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger size="small" icon={<DeleteOutlined />}>
              Remove
            </Button>
          </Popconfirm>
        ),
      },
    ];
  };

  // ---- Shared model form (Add & Edit) ----

  const buildPricingMap = (rows, hasAudio, isOptional) => {
    const map = {};
    (rows || []).forEach(row => {
      if (!row.resKey) return;
      if (hasAudio && isOptional) {
        map[row.resKey] = { audio_on: row.audioOn ?? 0, audio_off: row.audioOff ?? 0 };
      } else {
        map[row.resKey] = row.audioOn ?? 0;
      }
    });
    return map;
  };

  const pricingMapToRows = (map) =>
    Object.entries(map || {}).map(([resKey, val]) => ({
      resKey,
      audioOn: typeof val === 'object' ? val.audio_on : val,
      audioOff: typeof val === 'object' ? val.audio_off : null,
    }));

  const renderModelForm = (dialogState, setDialogState) => (
    <Form layout="vertical" style={{ marginTop: 16 }}>
      <Form.Item label="Model ID" required>
        <Input
          value={dialogState.modelId}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelId: e.target.value }))}
          placeholder="e.g., seedance-v1-5-pro-text-to-video"
        />
      </Form.Item>
      <Form.Item label="Model Name" required>
        <Input
          value={dialogState.modelName}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelName: e.target.value }))}
          placeholder="e.g., Seedance 1.5 Pro"
        />
      </Form.Item>
      <Form.Item label="Model Endpoint">
        <Input
          value={dialogState.modelEndpoint}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelEndpoint: e.target.value }))}
          placeholder="e.g., https://api.example.com/v1/generate (optional)"
        />
      </Form.Item>
      <Form.Item label="Model Icon">
        {dialogState.icon && (
          <div style={{ marginBottom: 8 }}>
            <img
              src={dialogState.icon}
              alt="Current icon"
              style={{
                width: 64,
                height: 64,
                objectFit: 'cover',
                borderRadius: 4,
                border: '1px solid #d9d9d9',
              }}
            />
            <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>Current icon</div>
          </div>
        )}
        <Upload
          accept="image/*,image/svg+xml"
          maxCount={1}
          beforeUpload={(file) => {
            const isImage = file.type.startsWith('image/') || file.type === 'image/svg+xml';
            if (!isImage) { message.error('Only image files allowed!'); return Upload.LIST_IGNORE; }
            if (file.size / 1024 / 1024 >= 5) { message.error('Max 5MB'); return Upload.LIST_IGNORE; }
            setDialogState((prev) => ({ ...prev, iconFile: file }));
            return false;
          }}
          onRemove={() => setDialogState((prev) => ({ ...prev, iconFile: null }))}
        >
          <Button icon={<UploadOutlined />}>
            {dialogState.icon ? 'Change Icon' : 'Upload Icon (Optional)'}
          </Button>
        </Upload>
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          Upload a new icon to replace the current one (SVG, PNG, JPG - max 5MB)
        </p>
      </Form.Item>
      <Form.Item label="Cost Preview">
        <Slider
          min={1}
          max={5}
          value={dialogState.costPreview || 1}
          onChange={(val) => setDialogState((prev) => ({ ...prev, costPreview: val }))}
          marks={{ 1: '$', 2: '$$', 3: '$$$', 4: '$$$$', 5: '$$$$$' }}
        />
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          Visual indicator of relative cost (1-5)
        </p>
      </Form.Item>
      <Form.Item label="Description">
        <Input.TextArea
          value={dialogState.modelDescription}
          rows={3}
          onChange={(e) => setDialogState((prev) => ({ ...prev, modelDescription: e.target.value }))}
          placeholder="Brief description of the model (optional)"
        />
      </Form.Item>
      <Divider />
      <Form.Item label="Has Audio Feature">
        <Switch
          checked={dialogState.hasAudioFeature}
          onChange={(val) => setDialogState((prev) => ({ ...prev, hasAudioFeature: val }))}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          Does this model support audio generation?
        </p>
      </Form.Item>
      {dialogState.hasAudioFeature && (
        <Form.Item label="Is Audio Optional">
          <Switch
            checked={dialogState.isAudioOptional}
            onChange={(val) => setDialogState((prev) => ({ ...prev, isAudioOptional: val }))}
            checkedChildren="Optional"
            unCheckedChildren="Always On"
          />
          <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
            If optional, users can choose with/without audio (separate costs).
          </p>
        </Form.Item>
      )}
      <Form.Item label="Is Free Crop Image">
        <Switch
          checked={dialogState.isFreeCropImage}
          onChange={(val) => setDialogState((prev) => ({ ...prev, isFreeCropImage: val }))}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          Enable free crop for image input (Image to Video). If disabled, uses the same crop behavior as Text to Video.
        </p>
      </Form.Item>
      <Form.Item label="Badge">
        <Input
          value={dialogState.badge || ''}
          placeholder="e.g., New, Popular (optional)"
          allowClear
          onChange={(e) => setDialogState((prev) => ({ ...prev, badge: e.target.value || null }))}
          style={{ width: 200 }}
        />
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          Optional badge text shown on this model in the app.
        </p>
      </Form.Item>
      <Divider />
      <Form.Item label="Resolution Pricing ($/sec)">
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          style={{ marginBottom: 12 }}
          onClick={() => setDialogState(prev => ({
            ...prev,
            pricingRows: [...(prev.pricingRows || []), { resKey: null, audioOn: null, audioOff: null }],
          }))}
        >
          Add Pricing Row
        </Button>
        {(dialogState.pricingRows || []).length > 0 && (
          <Table
            dataSource={dialogState.pricingRows}
            rowKey={(_, i) => i}
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Resolution',
                key: 'resolution',
                render: (_, row, i) => {
                  const usedKeys = new Set((dialogState.pricingRows || []).map(r => r.resKey).filter(Boolean));
                  return (
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      value={row.resKey}
                      placeholder="Select resolution"
                      onChange={(val) => setDialogState(prev => {
                        const rows = JSON.parse(JSON.stringify(prev.pricingRows));
                        rows[i] = { ...rows[i], resKey: val };
                        return { ...prev, pricingRows: rows };
                      })}
                    >
                      {getResolutionOptionsOrdered().map(key => (
                        <Option key={key} value={key} disabled={usedKeys.has(key) && key !== row.resKey}>
                          {VideoQualityEnum[key]?.name || key}
                        </Option>
                      ))}
                    </Select>
                  );
                },
              },
              ...(dialogState.hasAudioFeature && dialogState.isAudioOptional ? [
                {
                  title: 'OFF ($/sec)',
                  key: 'audioOff',
                  width: 120,
                  render: (_, row, i) => (
                    <InputNumber
                      size="small" min={0} step={0.0001} style={{ width: '100%' }}
                      value={row.audioOff}
                      onChange={(val) => setDialogState(prev => {
                        const rows = JSON.parse(JSON.stringify(prev.pricingRows));
                        rows[i] = { ...rows[i], audioOff: val };
                        return { ...prev, pricingRows: rows };
                      })}
                    />
                  ),
                },
                {
                  title: 'ON ($/sec)',
                  key: 'audioOn',
                  width: 120,
                  render: (_, row, i) => (
                    <InputNumber
                      size="small" min={0} step={0.0001} style={{ width: '100%' }}
                      value={row.audioOn}
                      onChange={(val) => setDialogState(prev => {
                        const rows = JSON.parse(JSON.stringify(prev.pricingRows));
                        rows[i] = { ...rows[i], audioOn: val };
                        return { ...prev, pricingRows: rows };
                      })}
                    />
                  ),
                },
              ] : [
                {
                  title: dialogState.hasAudioFeature ? 'ON ($/sec)' : '$/sec',
                  key: 'price',
                  width: 120,
                  render: (_, row, i) => (
                    <InputNumber
                      size="small" min={0} step={0.0001} style={{ width: '100%' }}
                      value={row.audioOn}
                      onChange={(val) => setDialogState(prev => {
                        const rows = JSON.parse(JSON.stringify(prev.pricingRows));
                        rows[i] = { ...rows[i], audioOn: val };
                        return { ...prev, pricingRows: rows };
                      })}
                    />
                  ),
                },
              ]),
              {
                title: 'Action',
                key: 'action',
                width: 60,
                render: (_, __, i) => (
                  <Button
                    danger size="small" type="text" icon={<DeleteOutlined />}
                    onClick={() => setDialogState(prev => ({
                      ...prev,
                      pricingRows: prev.pricingRows.filter((_, idx) => idx !== i),
                    }))}
                  />
                ),
              },
            ]}
          />
        )}
      </Form.Item>
      <Divider />
      <Form.Item label="Mark as Default Model">
        <Switch
          checked={dialogState.isDefault}
          onChange={(val) => setDialogState((prev) => ({ ...prev, isDefault: val }))}
          checkedChildren="Default"
          unCheckedChildren="Not Default"
        />
        <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
          Mark this model as one of the default models (maximum 2 default models per operation type).
        </p>
      </Form.Item>
      {dialogState.isDefault && (
        <>
          <Divider>
            <Space>
              <SettingOutlined />
              <span style={{ fontWeight: 600 }}>Default Selection Values</span>
            </Space>
          </Divider>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item label="Default Aspect Ratio">
              <Select
                value={dialogState.defaultValues?.aspectRatio}
                placeholder="Select aspect ratio"
                onChange={(val) =>
                  setDialogState((prev) => ({
                    ...prev,
                    defaultValues: { ...prev.defaultValues, aspectRatio: val },
                  }))
                }
              >
                {Object.entries(AspectRatioEnum).map(([key, val]) => (
                  <Option key={key} value={key}>
                    {val.name} ({val.ratio})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Default Resolution">
              <Select
                value={dialogState.defaultValues?.resolution}
                placeholder="Select resolution"
                onChange={(val) =>
                  setDialogState((prev) => ({
                    ...prev,
                    defaultValues: { ...prev.defaultValues, resolution: val },
                  }))
                }
              >
                {getResolutionOptionsOrdered().map((key) => (
                  <Option key={key} value={key}>
                    {VideoQualityEnum[key].name} ({VideoQualityEnum[key].width}x{VideoQualityEnum[key].height})
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Default Duration">
              <Select
                value={dialogState.defaultValues?.duration}
                placeholder="Select duration"
                onChange={(val) =>
                  setDialogState((prev) => ({
                    ...prev,
                    defaultValues: { ...prev.defaultValues, duration: val },
                  }))
                }
              >
                {Object.entries(VideoSecondsEnum).map(([key, secs]) => (
                  <Option key={key} value={key}>
                    {secs} seconds
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Default Audio Setting">
              <Switch
                checked={dialogState.defaultValues?.isAudio}
                onChange={(val) =>
                  setDialogState((prev) => ({
                    ...prev,
                    defaultValues: { ...prev.defaultValues, isAudio: val },
                  }))
                }
                checkedChildren="Audio On"
                unCheckedChildren="Audio Off"
              />
              <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                Pre-select audio on/off when user picks this model
              </p>
            </Form.Item>
          </div>
        </>
      )}
    </Form>
  );

  // ---- Render ----

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1600px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Title level={2} style={{ color: 'black', margin: 0 }}>
          Unified Video Credit Configuration
        </Title>
        <Button
          icon={<SettingOutlined />}
          onClick={() => {
            setMinVersionInput(minVersion);
            setMinVersionModalVisible(true);
          }}
        >
          Set Minimum App Version{minVersion ? ` (${minVersion})` : ''}
        </Button>
      </div>

      <Modal
        title="Set Minimum App Version for Unified Configs"
        open={minVersionModalVisible}
        onOk={handleSaveMinVersion}
        onCancel={() => setMinVersionModalVisible(false)}
        okText="Save"
        okButtonProps={{ disabled: minVersionInput === minVersion }}
        destroyOnClose
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Minimum App Version"
            extra="Users on this version or above will receive the unified credit config."
          >
            <Input
              value={minVersionInput}
              onChange={(e) => setMinVersionInput(e.target.value)}
              placeholder="e.g., 3.2.0"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Models card */}
      <Card
        title={<span style={{ fontSize: 18, fontWeight: 600 }}>Models</span>}
        extra={
          <Space>
            <Button icon={<SaveOutlined />} onClick={handleSave}>
              Save Changes
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setAddModelDialog({ ...EMPTY_MODEL_DIALOG, visible: true })}
            >
              Add Model
            </Button>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {!unifiedConfig.models || unifiedConfig.models.length === 0 ? (
          <Empty description="No models configured yet" style={{ padding: '40px' }} />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleModelDragEnd}
          >
            <SortableContext
              items={unifiedConfig.models.map((m) => m.id)}
              strategy={verticalListSortingStrategy}
            >
              {unifiedConfig.models.map((model, modelIndex) => (
                <SortableModelWrapper key={model.id} id={model.id}>
                  <Collapse
                    style={{ flex: 1, border: 'none', borderRadius: 0 }}
                    expandIconPosition="end"
                    items={[{
                      key: '0',
                      label: (
                        <div>
                          <Space align="start">
                            {model.icon ? (
                              <img
                                src={model.icon}
                                alt={model.name}
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 4,
                                  objectFit: 'cover',
                                  backgroundColor: '#f0f0f0',
                                  border: '1px solid #d9d9d9',
                                }}
                              />
                            ) : (
                              <Avatar
                                size={48}
                                shape="square"
                                style={{ marginTop: 4, backgroundColor: '#f0f0f0' }}
                                icon={<SettingOutlined />}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <Space wrap>
                                <Badge status="processing" color="#52c41a" />
                                <strong>{model.name}</strong>
                                <Tag color="purple">{model.id}</Tag>
                                {model.badge && (
                                  <Tag color="magenta">{model.badge}</Tag>
                                )}
                                {model.is_default && (
                                  <Tag color="gold">Default Model</Tag>
                                )}
                                {model.has_audio_feature ? (
                                  <Tag color="orange">
                                    Audio{' '}
                                    {model.is_audio_optional !== false
                                      ? '(Optional)'
                                      : '(Required)'}
                                  </Tag>
                                ) : (
                                  <Tag color="default">No Audio</Tag>
                                )}
                                {model.cost_preview && (
                                  <Tag color="cyan">
                                    {'$'.repeat(model.cost_preview)}
                                  </Tag>
                                )}
                              </Space>
                              {model.description && (
                                <div
                                  style={{
                                    marginTop: 8,
                                    fontSize: 13,
                                    fontWeight: 'normal',
                                    color: '#666',
                                  }}
                                >
                                  {model.description}
                                </div>
                              )}
                              {model.model_endpoint && (
                                <div
                                  style={{
                                    marginTop: 6,
                                    fontSize: 12,
                                    fontWeight: 'normal',
                                    color: '#999',
                                  }}
                                >
                                  <strong>Endpoint:</strong> {model.model_endpoint}
                                </div>
                              )}
                            </div>
                          </Space>
                        </div>
                      ),
                      extra: (
                        <Space>
                          <Tooltip title="Edit Model Settings">
                            <Button
                              type="default"
                              size="small"
                              icon={<SettingOutlined />}
                              onClick={() => {
                                setEditModelDialog({
                                  visible: true,
                                  modelIndex,
                                  modelId: model.id,
                                  modelName: model.name,
                                  modelDescription: model.description || '',
                                  modelEndpoint: model.model_endpoint || '',
                                  icon: model.icon || '',
                                  iconFile: null,
                                  costPreview: model.cost_preview || 1,
                                  hasAudioFeature: model.has_audio_feature ?? false,
                                  isAudioOptional: model.is_audio_optional ?? true,
                                  isDefault: model.is_default === true,
                                  isFreeCropImage: model.is_free_crop_image ?? false,
                                  badge: model.badge || null,
                                  defaultValues: model.default_values || {
                                    isAudio: false,
                                    duration: null,
                                    resolution: null,
                                    aspectRatio: null,
                                  },
                                  pricingRows: pricingMapToRows(model.pricing_by_resolution),
                                });
                              }}
                            >
                              Edit
                            </Button>
                          </Tooltip>
                          <Popconfirm
                            title="Remove Model"
                            description={`Remove ${model.name}?`}
                            onConfirm={() => handleRemoveModel(modelIndex)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button danger size="small" icon={<DeleteOutlined />}>
                              Remove
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                      children: (
                        <>
                          {/* Global Selectors */}
                      {(() => {
                        const activeRatios = new Set(model.aspect_ratios?.map(ar => ar.ratio?.api_enum_value) || []);
                        const allRatios = model.aspect_ratios || [];
                        const activeResolutions = new Set();
                        const allResKeys = new Set();
                        allRatios.forEach(ar => ar.resolutions?.forEach(r => allResKeys.add(r.resolution?.api_enum_value)));
                        allResKeys.forEach(resKey => {
                          if (allRatios.length > 0 && allRatios.every(ar => ar.resolutions?.some(r => r.resolution?.api_enum_value === resKey)))
                            activeResolutions.add(resKey);
                        });
                        const activeDurations = new Set();
                        const allResolutions = [];
                        allRatios.forEach(ar => ar.resolutions?.forEach(res => allResolutions.push(res)));
                        const allDurKeys = new Set();
                        allResolutions.forEach(res => res.durations?.forEach(d => allDurKeys.add(d.duration?.api_enum_value)));
                        allDurKeys.forEach(durKey => {
                          if (allResolutions.length > 0 && allResolutions.every(res => res.durations?.some(d => d.duration?.api_enum_value === durKey)))
                            activeDurations.add(durKey);
                        });
                        return (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>ASPECT RATIOS</div>
                              <Space wrap size={8}>
                                {Object.entries(AspectRatioEnum).map(([key, val]) => (
                                  <Button
                                    key={key}
                                    size="small"
                                    onClick={() => handleToggleAspectRatioGlobal(modelIndex, key)}
                                    style={activeRatios.has(key) ? { borderColor: '#1677ff', color: '#1677ff', background: '#e6f4ff' } : {}}
                                  >
                                    {val.ratio}
                                  </Button>
                                ))}
                              </Space>
                            </div>
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>RESOLUTIONS</div>
                              <Space wrap size={8}>
                                {getResolutionOptionsOrdered().map(key => (
                                  <Button
                                    key={key}
                                    size="small"
                                    onClick={() => handleToggleResolutionGlobal(modelIndex, key)}
                                    style={activeResolutions.has(key) ? { borderColor: '#52c41a', color: '#52c41a', background: '#f6ffed' } : {}}
                                  >
                                    {VideoQualityEnum[key].name}
                                  </Button>
                                ))}
                              </Space>
                            </div>
                            <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', letterSpacing: 1, marginBottom: 8 }}>DURATIONS</div>
                              <Space wrap size={8}>
                                {Object.entries(VideoSecondsEnum).map(([key, secs]) => (
                                  <Button
                                    key={key}
                                    size="small"
                                    onClick={() => handleToggleDurationGlobal(modelIndex, key)}
                                    style={activeDurations.has(key) ? { borderColor: '#fa8c16', color: '#fa8c16', background: '#fff7e6' } : {}}
                                  >
                                    {secs}s
                                  </Button>
                                ))}
                              </Space>
                            </div>
                          </div>
                        );
                      })()}
                      {/* Aspect Ratios */}
                      {model.aspect_ratios && model.aspect_ratios.length > 0 && (
                        <Collapse
                          accordion
                          expandIconPosition="end"
                          items={model.aspect_ratios.map((ar, ratioIndex) => ({
                            key: ratioIndex,
                            label: (
                              <Space>
                                <Badge status="processing" color="#1890ff" />
                                <strong>
                                  {AspectRatioEnum[ar.ratio?.api_enum_value]?.name ||
                                    ar.ratio?.api_enum_value}
                                </strong>
                                <Tag>{ar.ratio?.name}</Tag>
                              </Space>
                            ),
                            extra: (
                              <Popconfirm
                                title="Remove Aspect Ratio"
                                description={`Remove ${ar.ratio?.name}?`}
                                onConfirm={(e) => {
                                  e.stopPropagation();
                                  handleRemoveAspectRatio(modelIndex, ratioIndex);
                                }}
                                okText="Yes"
                                cancelText="No"
                                onCancel={(e) => e.stopPropagation()}
                              >
                                <Button
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Remove
                                </Button>
                              </Popconfirm>
                            ),
                            children: (
                              <>
                              {/* Resolutions */}
                              {ar.resolutions?.map((res, resIndex) => {
                                const hasAudio = model.has_audio_feature;
                                const isOptional = model.is_audio_optional !== false;
                                const pricing = res.pricing_per_second;

                                return (
                                  <Card
                                    key={resIndex}
                                    type="inner"
                                    size="small"
                                    title={
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          flexWrap: 'wrap',
                                        }}
                                      >
                                        <Tag color="green">{res.resolution?.name}</Tag>
                                        <Tag color="blue">{res.resolution?.api_enum_value}</Tag>
                                        {pricing !== undefined &&
                                          pricing !== null &&
                                          (() => {
                                            if (
                                              hasAudio &&
                                              typeof pricing === 'object'
                                            ) {
                                              return (
                                                <>
                                                  {pricing.audio_off !== undefined && (
                                                    <Tag color="orange" style={{ fontSize: 11 }}>
                                                      OFF: {pricing.audio_off} $/sec
                                                    </Tag>
                                                  )}
                                                  {pricing.audio_on !== undefined && (
                                                    <Tag color="purple" style={{ fontSize: 11 }}>
                                                      ON: {pricing.audio_on} $/sec
                                                    </Tag>
                                                  )}
                                                </>
                                              );
                                            }
                                            if (typeof pricing === 'number') {
                                              return (
                                                <Tag color="purple" style={{ fontSize: 11 }}>
                                                  {pricing} $/sec
                                                </Tag>
                                              );
                                            }
                                            return null;
                                          })()}
                                      </div>
                                    }
                                    extra={
                                      <Popconfirm
                                        title="Remove Resolution"
                                        description={`Remove ${res.resolution?.name}?`}
                                        onConfirm={() =>
                                          handleRemoveResolution(modelIndex, ratioIndex, resIndex)
                                        }
                                        okText="Yes"
                                        cancelText="No"
                                      >
                                        <Button danger size="small" icon={<DeleteOutlined />}>
                                          Remove
                                        </Button>
                                      </Popconfirm>
                                    }
                                    style={{ marginBottom: 16 }}
                                  >

                                    <Table
                                      columns={createDurationColumns(
                                        modelIndex,
                                        ratioIndex,
                                        resIndex,
                                        hasAudio,
                                        isOptional
                                      )}
                                      dataSource={res.durations || []}
                                      rowKey={(record) =>
                                        record.duration?.api_enum_value || Math.random()
                                      }
                                      pagination={false}
                                      size="small"
                                      bordered
                                    />
                                    {(() => {
                                      const existingKeys = new Set(res.durations?.map(d => d.duration?.api_enum_value) || []);
                                      const available = Object.entries(VideoSecondsEnum).filter(([k]) => !existingKeys.has(k));
                                      if (available.length === 0) return null;
                                      return (
                                        <Select
                                          size="small"
                                          style={{ width: '100%', marginTop: 8 }}
                                          placeholder="+ Add duration to this resolution..."
                                          value={null}
                                          onChange={(durationKey) => {
                                            const seconds = VideoSecondsEnum[durationKey];
                                            const newDur = {
                                              duration: { api_enum_value: durationKey, seconds, name: `${seconds} seconds`, short_name: `${seconds}s` },
                                              cost: 0,
                                            };
                                            setUnifiedConfig((prev) => {
                                              const nm = JSON.parse(JSON.stringify(prev.models));
                                              const resObj = nm[modelIndex].aspect_ratios[ratioIndex].resolutions[resIndex];
                                              resObj.durations = [...(resObj.durations || []), newDur].sort(
                                                (a, b) => a.duration.seconds - b.duration.seconds
                                              );
                                              return { models: nm };
                                            });
                                          }}
                                        >
                                          {available.map(([key, secs]) => (
                                            <Option key={key} value={key}>{secs}s — {key}</Option>
                                          ))}
                                        </Select>
                                      );
                                    })()}
                                  </Card>
                                );
                              })}

                              </>
                            ),
                          }))}
                        />
                      )}

                        </>
                      ),
                    }]}
                  />
                </SortableModelWrapper>
              ))}
            </SortableContext>
          </DndContext>
        )}

      </Card>

      {/* Add Model Modal */}
      <Modal
        title="Add Model"
        open={addModelDialog.visible}
        onOk={handleAddModel}
        onCancel={() => setAddModelDialog(EMPTY_MODEL_DIALOG)}
        width={700}
        destroyOnClose
      >
        {renderModelForm(addModelDialog, setAddModelDialog)}
      </Modal>

      {/* Edit Model Modal */}
      <Modal
        title="Edit Model"
        open={editModelDialog.visible}
        onOk={handleEditModel}
        onCancel={() => setEditModelDialog({ ...EMPTY_MODEL_DIALOG, modelIndex: -1 })}
        width={700}
        destroyOnClose
      >
        {renderModelForm(editModelDialog, setEditModelDialog)}
      </Modal>

    </div>
  );
};

export default UnifiedCreditConfig;
